import { useState, useEffect } from 'react';
import { Group } from '../../components/types';
import { useHistory } from './useHistory';
import { useGroupsManagement } from './useGroupsManagement';
import { useSectionsManagement } from './useSectionsManagement';
import { useQuestionsManagement } from './useQuestionsManagement';
import { useDragAndDropLogic } from './useDragAndDropLogic';
import { tagService, Tag } from '@/services/checklists';

export function useChecklistWorkspace() {
  const [groups, setGroups] = useState<Group[]>([]);
  const [idMapping, setIdMapping] = useState<Map<string, string>>(new Map());
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);

  useEffect(() => {
    const loadTags = async () => {
      try {
        const tags = await tagService.getAllActiveTags();
        setAvailableTags(tags);
      } catch {
        // Ignore errors loading tags
      }
    };
    loadTags();
  }, []);

  const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const loadInitialData = (initialGroups: Group[]) => {
    const mapping = new Map<string, string>();

    const transformedGroups = initialGroups.map((group) => {
      const newGroupId = generateId();
      mapping.set(newGroupId, String(group.id));

      return {
        ...group,
        id: newGroupId,
        sections: group.sections.map((section) => {
          const newSectionId = generateId();
          mapping.set(newSectionId, String(section.id));

          return {
            ...section,
            id: newSectionId,
            questions: section.questions.map((question) => {
              const newQuestionId = generateId();
              mapping.set(newQuestionId, String(question.id));

              return {
                ...question,
                id: newQuestionId,
              };
            }),
          };
        }),
      };
    });

    setIdMapping(mapping);
    setGroups(transformedGroups);
  };

  const historyHook = useHistory(groups);
  const { saveToHistory, undo: undoHistory, redo: redoHistory, canUndo, canRedo } = historyHook;

  const undo = () => {
    const previousState = undoHistory();
    if (previousState) {
      setGroups(previousState);
    }
  };

  const redo = () => {
    const nextState = redoHistory();
    if (nextState) {
      setGroups(nextState);
    }
  };

  const groupsManagement = useGroupsManagement({
    groups,
    setGroups,
    saveToHistory,
    generateId,
  });

  const sectionsManagement = useSectionsManagement({
    groups,
    setGroups,
    saveToHistory,
    generateId,
  });

  const questionsManagement = useQuestionsManagement({
    groups,
    setGroups,
    saveToHistory,
    generateId,
  });

  const dragAndDropLogic = useDragAndDropLogic({
    groups,
    setGroups,
    saveToHistory,
    generateId,
  });

  const formatForBackend = () => {
    return {
      groups: groups.map((group, groupIndex) => {
        const originalGroupId = idMapping.get(group.id);

        return {
          ...(originalGroupId ? { id: originalGroupId } : {}),
          name: group.name,
          order: groupIndex,
          sections: group.sections.map((section, sectionIndex) => {
            const originalSectionId = idMapping.get(section.id);

            return {
              ...(originalSectionId ? { id: originalSectionId } : {}),
              name: section.name,
              order: sectionIndex,
              questions: section.questions.map((question, questionIndex) => {
                const originalQuestionId = idMapping.get(question.id);
                const { id, ...questionData } = question;

            const cleanedQuestion: any = {
              ...(originalQuestionId ? { id: originalQuestionId } : {}),
              ...questionData,
              order: questionIndex,
            };

            if (!cleanedQuestion.hasHint) {
              delete cleanedQuestion.hintText;
            }
            if (!cleanedQuestion.hasDisplayRule) {
              delete cleanedQuestion.displayRule;
            }
            if (!cleanedQuestion.options || cleanedQuestion.options.length === 0) {
              delete cleanedQuestion.options;
            }
            if (!cleanedQuestion.maxCharacters) {
              delete cleanedQuestion.maxCharacters;
            } else {
              cleanedQuestion.maxCharacters = Number(cleanedQuestion.maxCharacters);
            }
            if (!cleanedQuestion.decimalPlaces) {
              delete cleanedQuestion.decimalPlaces;
            } else {
              cleanedQuestion.decimalPlaces = Number(cleanedQuestion.decimalPlaces);
            }
            if (!cleanedQuestion.maxPhotos) {
              delete cleanedQuestion.maxPhotos;
            } else {
              cleanedQuestion.maxPhotos = Number(cleanedQuestion.maxPhotos);
            }
            if (cleanedQuestion.allowGallery !== undefined) {
              cleanedQuestion.allowGallerySelection = cleanedQuestion.allowGallery;
              delete cleanedQuestion.allowGallery;
            } else {
              delete cleanedQuestion.allowGallerySelection;
            }
            if (!cleanedQuestion.maxFiles) {
              delete cleanedQuestion.maxFiles;
            } else {
              cleanedQuestion.maxFiles = Number(cleanedQuestion.maxFiles);
            }
            if (!cleanedQuestion.allowedFileTypes || cleanedQuestion.allowedFileTypes.length === 0) {
              delete cleanedQuestion.allowedFileTypes;
            }
            if (cleanedQuestion.columns && cleanedQuestion.columns.length > 0) {
              cleanedQuestion.tableColumns = cleanedQuestion.columns.map((col: any, index: number) => {
                if (typeof col === 'string') {
                  return { name: col, order: index };
                }
                return {
                  name: col.name || col,
                  order: col.order !== undefined ? col.order : index
                };
              });
              delete cleanedQuestion.columns;
            } else {
              delete cleanedQuestion.tableColumns;
              delete cleanedQuestion.columns;
            }
            if (cleanedQuestion.tags && cleanedQuestion.tags.length > 0) {
              const tagNames = cleanedQuestion.tags.map((tag: any) => {
                if (typeof tag === 'string') {
                  return tag;
                } else if (tag && typeof tag === 'object' && tag.name) {
                  return tag.name;
                }
                return null;
              }).filter((name: string | null): name is string => name !== null);

              const tagIds = tagNames
                .map((tagName: string) => {
                  const tag = availableTags.find(t => t.name === tagName);
                  return tag?.id;
                })
                .filter((id: string | undefined): id is string => id !== undefined);

              if (tagIds.length > 0) {
                cleanedQuestion.tagIds = tagIds;
              }
              delete cleanedQuestion.tags;
            } else {
              delete cleanedQuestion.tags;
            }
            if (!cleanedQuestion.template) {
              delete cleanedQuestion.template;
            }
            if (!cleanedQuestion.description) {
              delete cleanedQuestion.description;
            }

            return cleanedQuestion;
              }),
            };
          }),
        };
      }),
    };
  };

  return {
    groups,
    loadInitialData,

    undo,
    redo,
    canUndo,
    canRedo,

    addGroup: groupsManagement.addGroup,
    editGroup: groupsManagement.editGroup,
    removeGroup: groupsManagement.removeGroup,
    duplicateGroup: groupsManagement.duplicateGroup,

    addSection: sectionsManagement.addSection,
    editSection: sectionsManagement.editSection,
    removeSection: sectionsManagement.removeSection,
    duplicateSection: sectionsManagement.duplicateSection,

    addQuestion: questionsManagement.addQuestion,
    editQuestion: questionsManagement.editQuestion,
    removeQuestion: questionsManagement.removeQuestion,
    duplicateQuestion: questionsManagement.duplicateQuestion,

    handleDragEnd: dragAndDropLogic.handleDragEnd,

    formatForBackend,
  };
}
